import React, { useState, useEffect } from "react";
import { getAllAnnouncements, deleteAnnouncement } from "../../../api/index.js";
import Pagination from "../../components/Pagination/Pagination";
import ButtonIcon from "../../components/ButtonIcon/ButtonIcon";
import PlusIcon from "../../assets/svg/icons/common/Plus";
import CreateAnnouncementModal from "./components/create-announcement-modal";
import ConfirmationModal from "../application/components/ConfirmationModal/ConfirmationModal";
import Trash from "../../assets/svg/icons/common/Trash";
import { toast } from "react-hot-toast";
import useUserStore from "../../stores/user";
import SkeletonLoader from "../../components/skeleton-loader";
import style from "./Announcements.module.scss";

const Announcements = () => {
  const [expandedRow, setExpandedRow] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [announcementToEdit, setAnnouncementToEdit] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [announcementToDelete, setAnnouncementToDelete] = useState(null);
  const { isPCAdmin } = useUserStore();

  useEffect(() => {
    fetchAnnouncements();
  }, [currentPage]);

  const mapAnnouncement = (announcement) => ({
    id: announcement.id,
    category: announcement.category,
    categoryType: getCategoryType(announcement.category),
    title: announcement.title,
    description: announcement.description || "",
    target_type: announcement.target_type,
    starts_at: announcement.starts_at,
    expires_at: announcement.expires_at,
    dateModified: formatDate(announcement.updated_at || announcement.created_at),
    targets: announcement.targets || [],
    children: [
      {
        id: `${announcement.id}-1`,
        title: announcement.title,
        description: announcement.description || "",
        target_type: announcement.target_type,
        targets: announcement.targets || [],
      },
    ],
  });

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const response = await getAllAnnouncements({ per_page: itemsPerPage, page: currentPage });

      if ((response?.success || response?.status) && response?.data) {
        const paginatedData = response.data;
        const mappedAnnouncements = (paginatedData.data || []).map(mapAnnouncement);

        setAnnouncements(mappedAnnouncements);
        setTotalPages(paginatedData.last_page || 1);
        setTotalItems(paginatedData.total || 0);
        setItemsPerPage(paginatedData.per_page || 10);
      }
    } catch (error) {
      console.error("Error fetching announcements:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const day = date.getDate().toString().padStart(2, "0");
      const year = date.getFullYear();
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? "p.m." : "a.m.";
      const displayHours = hours % 12 || 12;
      const displayMinutes = minutes.toString().padStart(2, "0");
      return `${month}/${day}/${year} ${displayHours}:${displayMinutes} ${ampm}`;
    } catch (e) {
      return dateString;
    }
  };

  const getCategoryType = (category) => {
    const categoryLower = category?.toLowerCase() || "";
    if (categoryLower.includes("release")) return "success";
    if (categoryLower.includes("alert")) return "danger";
    if (categoryLower.includes("bug") || categoryLower.includes("fix")) return "info";
    return "success";
  };


  const toggleRow = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const getBadgeClass = (type) => {
    switch (type) {
      case "success":
        return style.badge_success;
      case "danger":
        return style.badge_danger;
      case "info":
        return style.badge_info;
      default:
        return style.badge_success;
    }
  };

  const handleCreateSuccess = () => {
    fetchAnnouncements();
  };

  const handleEditClick = (e, announcement) => {
    e.stopPropagation();
    setAnnouncementToEdit(announcement);
    setIsEditModalOpen(true);
  };

  const handleEditSuccess = () => {
    fetchAnnouncements();
    setIsEditModalOpen(false);
    setAnnouncementToEdit(null);
  };

  const handleDeleteClick = (e, announcement) => {
    e.stopPropagation();
    setAnnouncementToDelete(announcement);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!announcementToDelete) return;

    const toastId = toast.loading("Deleting announcement...");
    try {
      const response = await deleteAnnouncement(announcementToDelete.id);
      if (response?.success) {
        toast.success("Announcement deleted successfully", { id: toastId });
        setDeleteModalOpen(false);
        setAnnouncementToDelete(null);
        fetchAnnouncements();
      } else {
        toast.error(response?.message || "Failed to delete announcement", { id: toastId });
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to delete announcement", { id: toastId });
    }
  };

  const isAdminUser = isPCAdmin;

  return (
    <div className={style.announcements_container}>
      <div className={style.announcements_head}>
        <h4 className={style.announcements_title}>Announcements</h4>
        {isAdminUser && (
          <ButtonIcon
            bgColor="#4C70EF"
            textColor="#FFF"
            onClick={() => setIsCreateModalOpen(true)}
          >
            <PlusIcon />
            <span>Create Announcement</span>
          </ButtonIcon>
        )}
      </div>

      <div className={style.announcements_table}>
        <div className={style.table_header_row}>
          <div className={style.table_header_cell}></div>
          <div className={style.table_header_cell}>
            <span className={style.header_text}>CATEGORY</span>
          </div>
          <div className={style.table_header_cell}>
            <span className={style.header_text}>TITLE</span>
          </div>
          <div className={style.table_header_cell}>
            <span className={style.header_text}>DESCRIPTION</span>
          </div>
          <div className={style.table_header_cell}>
            <span className={style.header_text}>DATE MODIFIED</span>
          </div>
          <div className={style.table_header_cell}></div>
        </div>

        {loading ? (
          Array.from({ length: 8 }).map((_, index) => (
            <div key={`skeleton-${index}`} className={style.skeleton_row}>
              <div className={style.table_body_cell_icon} />
              <div className={style.table_body_cell}>
                <SkeletonLoader variant="rectangular" height={24} width={100} />
              </div>
              <div className={style.table_body_cell}>
                <SkeletonLoader variant="rectangular" height={20} width="80%" />
              </div>
              <div className={style.table_body_cell}>
                <SkeletonLoader variant="rectangular" height={20} width="100%" />
              </div>
              <div className={style.table_body_cell}>
                <SkeletonLoader variant="rectangular" height={20} width={120} />
              </div>
              <div className={style.table_body_cell_empty} />
            </div>
          ))
        ) : announcements.length === 0 ? (
          <div className={style.empty_state}>No announcements found</div>
        ) : (
          announcements.map((announcement) => (
          <React.Fragment key={announcement.id}>
            <div
              className={style.table_body_row}
              onClick={() => toggleRow(announcement.id)}
            >
              <div className={style.table_body_cell_icon}>
                {announcement.children && announcement.children.length > 0 && (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={expandedRow === announcement.id ? style.icon_rotated : ""}>
                    <path d="M11.3538 8.35372L6.35375 13.3537C6.3073 13.4002 6.25215 13.437 6.19145 13.4622C6.13076 13.4873 6.0657 13.5003 6 13.5003C5.93431 13.5003 5.86925 13.4873 5.80856 13.4622C5.74786 13.437 5.69271 13.4002 5.64625 13.3537C5.5998 13.3073 5.56295 13.2521 5.53781 13.1914C5.51267 13.1307 5.49973 13.0657 5.49973 13C5.49973 12.9343 5.51267 12.8692 5.53781 12.8085C5.56295 12.7478 5.5998 12.6927 5.64625 12.6462L10.2931 7.99997L5.64625 3.35372C5.55243 3.2599 5.49973 3.13265 5.49973 2.99997C5.49973 2.86729 5.55243 2.74004 5.64625 2.64622C5.74007 2.5524 5.86732 2.49969 6 2.49969C6.13269 2.49969 6.25993 2.5524 6.35375 2.64622L11.3538 7.64622C11.4002 7.69266 11.4371 7.7478 11.4623 7.8085C11.4874 7.8692 11.5004 7.93427 11.5004 7.99997C11.5004 8.06568 11.4874 8.13074 11.4623 8.19144C11.4371 8.25214 11.4002 8.30729 11.3538 8.35372Z" fill="#3A383F"/>
                  </svg>
                )}
              </div>
              <div className={style.table_body_cell}>
                <span className={getBadgeClass(announcement.categoryType)}>
                  {announcement.category}
                </span>
              </div>
              <div className={style.table_body_cell}>
                <span className={style.cell_text}>{announcement.title}</span>
              </div>
              <div className={style.table_body_cell}>
                <span className={`${style.cell_text} ${style.cell_text_description}`}>
                  {announcement.description}
                </span>
              </div>
              <div className={style.table_body_cell}>
                <span className={style.cell_text}>
                  {announcement.dateModified}
                </span>
              </div>
              <div className={style.table_body_cell_empty}>
                {isAdminUser && (
                  <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <button
                      className={style.edit_button}
                      onClick={(e) => handleEditClick(e, announcement)}
                      title="Edit announcement"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11.3333 2.00004C11.5084 1.82488 11.7163 1.68698 11.9444 1.59527C12.1726 1.50356 12.4161 1.46008 12.6611 1.46782C12.9061 1.47556 13.1471 1.53436 13.3691 1.6407C13.5911 1.74704 13.7893 1.89864 13.9519 2.08671C14.1145 2.27478 14.238 2.49535 14.3149 2.73487C14.3918 2.97439 14.4204 3.228 14.3989 3.47956C14.3774 3.73112 14.3062 3.97544 14.1897 4.19778C14.0732 4.42012 13.9141 4.61589 13.7222 4.77337L13.3333 5.16671L10.8333 2.66671L11.2222 2.27337C11.3147 2.18082 11.4245 2.10747 11.5459 2.05724C11.6673 2.00701 11.7979 1.98083 11.93 1.9801C12.0621 1.97937 12.193 2.0041 12.3151 2.05301C12.4372 2.10192 12.5481 2.17412 12.6417 2.26571L11.3333 2.00004ZM9.83333 3.50004L2.66667 10.6667V13.3334H5.33333L12.5 6.16671L9.83333 3.50004Z" fill="#A4A3A8"/>
                      </svg>
                    </button>
                  <button
                    className={style.delete_button}
                    onClick={(e) => handleDeleteClick(e, announcement)}
                    title="Delete announcement"
                  >
                    <Trash color="#A4A3A8" />
                  </button>
                  </div>
                )}
              </div>
            </div>

            {expandedRow === announcement.id && announcement.children && announcement.children.length > 0 ? (
              <div className={style.expanded_row}>
                <div className={style.expanded_tree}>
                  <svg width="17" height="29" viewBox="0 0 17 29" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 0V20C1 24.4183 4.58172 28 9 28H17" stroke="#C1C1DD" strokeWidth="2"/>
                  </svg>
                </div>
                {announcement.children.map((child, childIndex) => (
                  <React.Fragment key={child.id}>
                    <div className={style.expanded_body_row}>
                      <div className={style.expanded_cell_empty_left}></div>
                      <div className={style.expanded_cell_content}>
                        <div className={style.expanded_content_header}>
                        <h5 className={style.expanded_title}>
                          {child.title}
                        </h5>
                        </div>
                        <p className={style.expanded_description} style={{ whiteSpace: "pre-wrap" }}>
                          {child.description}
                        </p>
                        {child.target_type && (
                          <div className={style.expanded_targets_details}>
                            <div className={style.target_info_row}>
                              <span className={style.target_info_label}>Target Type:</span>
                              <span className={style.target_info_value}>
                                {child.target_type === "all" ? "All Users" : child.target_type === "organization" ? "Specific Organizations" : "Specific Users"}
                              </span>
                            </div>
                            {child.target_type !== "all" && child.targets && child.targets.length > 0 && (
                              <div className={style.target_info_row}>
                                <span className={style.target_info_label}>Targets:</span>
                                <div className={style.target_values_compact}>
                                  {child.target_type === "organization" ? (
                                    child.targets.map((target) => (
                                      <span key={target.id} className={style.target_value_item}>
                                        {target.organization?.name || `Org ${target.organization_id}`}
                                      </span>
                                    ))
                                  ) : (
                                    child.targets.map((target) => (
                                      <span key={target.id} className={style.target_value_item}>
                                        {target.user?.name || `User ${target.user_id}`}
                                      </span>
                                    ))
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className={style.expanded_cell_empty_right}></div>
                    </div>
                    {childIndex < announcement.children.length - 1 && (
                      <div className={style.expanded_divider}></div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            ) : null}
          </React.Fragment>
          ))
        )}

        {announcements.length > 0 && (
          <div className={style.table_pagination}>
            <Pagination
              pageCount={totalPages}
              currentPage={currentPage - 1}
              onPageChange={({ selected }) => {
                setCurrentPage(selected + 1);
              }}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
            />
          </div>
        )}
      </div>

      {isAdminUser && (
        <>
        <CreateAnnouncementModal
          open={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={handleCreateSuccess}
        />
          <CreateAnnouncementModal
            open={isEditModalOpen}
            onClose={() => {
              setIsEditModalOpen(false);
              setAnnouncementToEdit(null);
            }}
            onSuccess={handleEditSuccess}
            announcement={announcementToEdit}
            isEdit={true}
          />
        </>
      )}

      <ConfirmationModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setAnnouncementToDelete(null);
        }}
        message="Are you sure you want to delete this announcement? This action cannot be undone."
        onConfirmClick={handleDeleteConfirm}
        submitButtonText="Delete"
      />
    </div>
  );
};

export default Announcements;

